import { computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { useToast } from "vue-toast-notification";
import http from "@/services/http";
import { useAuthStore } from "@/store/auth";
import { getClients } from "@/services/clientService";

export type RenewalNotificationFilterId =
  | "due-this-month"
  | "renewals-next-month"
  | "overdue-renewals"
  | "expired-accounts";

export interface RenewalNotificationItem {
  id: string;
  clientId: string | number;
  clientRouteKey: string;
  companyName: string;
  title: string;
  message: string;
  tone: "danger" | "warning" | "info";
  filterId: RenewalNotificationFilterId;
  sortOrder: number;
  sortDays: number;
}

function titleCase(value: unknown, fallback = "") {
  const source = String(value || fallback).replace(/[_-]+/g, " ").trim();
  if (!source) return fallback;
  return source.replace(/\w\S*/g, (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function parseDateOnly(value: unknown) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;

  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function diffInDays(target: Date, base = new Date()) {
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate()).getTime();
  const end = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate()
  ).getTime();
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

function formatDateLabel(value: unknown) {
  const parsed = parseDateOnly(value);
  if (!parsed) return "an unscheduled date";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isNextMonth(date: Date, base = new Date()) {
  const nextMonth = new Date(base.getFullYear(), base.getMonth() + 1, 1);
  return (
    date.getFullYear() === nextMonth.getFullYear() &&
    date.getMonth() === nextMonth.getMonth()
  );
}

function isRenewalNotificationItem(
  item: RenewalNotificationItem | null
): item is RenewalNotificationItem {
  return item !== null;
}

function buildNotification(client: any): RenewalNotificationItem | null {
  const summary =
    client && typeof client.renewal_summary === "object" && client.renewal_summary
      ? client.renewal_summary
      : {};

  const companyName = String(
    client?.company_name || client?.name || "Unnamed Client"
  ).trim();
  const clientId = client?.id ?? client?.client_id;
  const clientRouteKey = typeof client?.route_key === "string" ? client.route_key.trim() : "";
  const renewalStatus = titleCase(
    summary.derived_status || client?.renewal_status,
    "Active"
  );
  const countdownDate = String(summary.countdown_date || client?.renewal_date || "").trim();
  const parsedCountdown = parseDateOnly(countdownDate);
  const daysUntilRenewal =
    typeof summary.days_until_renewal === "number"
      ? summary.days_until_renewal
      : parsedCountdown
        ? diffInDays(parsedCountdown)
        : null;
  const isOverdue =
    typeof summary.is_overdue === "boolean"
      ? summary.is_overdue
      : typeof daysUntilRenewal === "number"
        ? daysUntilRenewal < 0
        : false;

  if (clientId == null) {
    return null;
  }

  if (renewalStatus === "Expired") {
    return {
      id: `expired:${clientId}:${countdownDate || "none"}`,
      clientId,
      clientRouteKey,
      companyName,
      title: "Account expired",
      message: `${companyName} expired on ${formatDateLabel(
        countdownDate || client?.renewal_end_date
      )}.`,
      tone: "danger",
      filterId: "expired-accounts",
      sortOrder: 0,
      sortDays: -9999,
    };
  }

  if (isOverdue && typeof daysUntilRenewal === "number") {
    return {
      id: `overdue:${clientId}:${countdownDate || "none"}`,
      clientId,
      clientRouteKey,
      companyName,
      title: "Renewal overdue",
      message: `${companyName} is ${Math.abs(daysUntilRenewal)} day${
        Math.abs(daysUntilRenewal) === 1 ? "" : "s"
      } overdue.`,
      tone: "danger",
      filterId: "overdue-renewals",
      sortOrder: 1,
      sortDays: daysUntilRenewal,
    };
  }

  if (daysUntilRenewal === 0) {
    return {
      id: `today:${clientId}:${countdownDate || "none"}`,
      clientId,
      clientRouteKey,
      companyName,
      title: "Renewal due today",
      message: `${companyName} is due today, ${formatDateLabel(countdownDate)}.`,
      tone: "warning",
      filterId: "due-this-month",
      sortOrder: 2,
      sortDays: 0,
    };
  }

  if (typeof daysUntilRenewal === "number" && daysUntilRenewal > 0 && daysUntilRenewal <= 7) {
    return {
      id: `soon:${clientId}:${countdownDate || "none"}`,
      clientId,
      clientRouteKey,
      companyName,
      title: "Renewal due soon",
      message: `${companyName} renews in ${daysUntilRenewal} day${
        daysUntilRenewal === 1 ? "" : "s"
      } on ${formatDateLabel(countdownDate)}.`,
      tone: "warning",
      filterId: "due-this-month",
      sortOrder: 3,
      sortDays: daysUntilRenewal,
    };
  }

  if (parsedCountdown && isNextMonth(parsedCountdown)) {
    return {
      id: `next:${clientId}:${countdownDate || "none"}`,
      clientId,
      clientRouteKey,
      companyName,
      title: "Renewal next month",
      message: `${companyName} is scheduled for ${formatDateLabel(countdownDate)}.`,
      tone: "info",
      filterId: "renewals-next-month",
      sortOrder: 4,
      sortDays: typeof daysUntilRenewal === "number" ? daysUntilRenewal : 9999,
    };
  }

  return null;
}

export function useRenewalNotifications() {
  const auth = useAuthStore();
  const toast = useToast();
  const cache = useQueryClient();
  const enabled = computed(() => auth.accountType === "internal_user" && ["1", "2"].includes(auth.userLevel));
  const stateKey = computed(() => ["renewal-notification-reads", auth.internalUser?.id]);
  const clientsKey = computed(() => ["renewal-notification-clients", auth.internalUser?.id]);
  const clients = useQuery({ queryKey: clientsKey, queryFn: getClients, enabled, refetchInterval: 60000 });
  const state = useQuery({
    queryKey: stateKey,
    queryFn: async () => (await http.get<{ seenIds: string[] }>("/api/renewal-notifications/read-state")).data,
    enabled,
    refetchInterval: 60000,
  });
  const seenIds = computed(() => enabled.value ? state.data.value?.seenIds ?? [] : []);
  const notifications = computed<RenewalNotificationItem[]>(() => {
    const rows = enabled.value && Array.isArray(clients.data.value?.data) ? clients.data.value.data : [];
    return rows.map(buildNotification).filter(isRenewalNotificationItem)
      .sort((a: RenewalNotificationItem, b: RenewalNotificationItem) => a.sortOrder - b.sortOrder || a.sortDays - b.sortDays);
  });
  const mutation = useMutation({
    mutationFn: async (input: { ids?: string[]; read?: boolean; key: unknown[] }) => {
      if (!input.ids) return (await http.delete("/api/renewal-notifications/read-state")).data;
      let result;
      for (let offset = 0; offset < input.ids.length; offset += 1000) {
        result = (await http.patch("/api/renewal-notifications/read-state", {
          ids: input.ids.slice(offset, offset + 1000), read: input.read,
        })).data;
      }
      return result;
    },
    onSuccess: (result, input) => { if (result) cache.setQueryData(input.key, result); },
    onError: () => { toast.error("Unable to save notification status. Please try again."); },
    onSettled: (_data, _error, input) => cache.invalidateQueries({ queryKey: input.key }),
  });
  function update(ids?: string[], read = true) {
    if (!enabled.value || mutation.isPending.value || (ids && !ids.length)) return;
    mutation.mutate({ ids, read, key: [...stateKey.value] });
  }
  const unreadNotifications = computed(() => notifications.value.filter(item => !seenIds.value.includes(item.id)));
  return {
    notifications, seenIds, unreadNotifications,
    unreadCount: computed(() => unreadNotifications.value.length),
    notificationsLoading: computed(() => enabled.value && (clients.isLoading.value || state.isLoading.value)),
    notificationsError: computed(() => clients.isError.value || state.isError.value),
    notificationsSaving: mutation.isPending,
    refreshNotifications: () => Promise.all([clients.refetch(), state.refetch()]),
    markNotificationAsRead: (id: string) => update([id]),
    markNotificationAsUnread: (id: string) => update([id], false),
    markAllNotificationsAsRead: () => update(unreadNotifications.value.map(item => item.id)),
    resetSeenNotifications: () => update(),
  };
}
